import React from 'react';
import { useAuth } from '../context/AuthContext';
import { SUBSCRIPTION_PLANS, SubscriptionTier } from '../types';

// Paid plans can't be bought yet (no billing for personal accounts), so their buttons say so
// instead of pretending to upgrade.
const PAID_PLANS_OPEN = false;

const Upgrade: React.FC = () => {
  const { user } = useAuth();

  const getTierColor = (tier: SubscriptionTier) => {
    switch (tier) {
      case 'social': return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'pro': return 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)';
      case 'premium': return 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)';
      default: return 'linear-gradient(135deg, #28a745 0%, #20c997 100%)';
    }
  };

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ margin: 0, fontSize: '32px', color: '#333' }}>🚀 Upgrade Your Mango</h1>
        <p style={{ margin: '15px 0 0 0', color: '#666', fontSize: '16px' }}>
          Unlock more features and get the most out of Mango
        </p>
        {!PAID_PLANS_OPEN && (
          <p style={{ margin: '15px auto 0', maxWidth: '560px', padding: '12px 16px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', color: '#9a3412', fontSize: '14px' }}>
            Paid plans aren't open yet. Everyone is on Free for now, and we'll let you know when you can upgrade.
          </p>
        )}
      </div>

      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '25px',
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {Object.values(SUBSCRIPTION_PLANS).map((plan) => {
          const isCurrentPlan = user?.subscription === plan.id;
          const isDowngrade = 
            (user?.subscription === 'premium' && plan.id !== 'premium') ||
            (user?.subscription === 'pro' && ['free', 'social'].includes(plan.id)) ||
            (user?.subscription === 'social' && plan.id === 'free');

          return (
            <div
              key={plan.id}
              style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: isCurrentPlan ? '0 8px 30px rgba(255, 107, 53, 0.3)' : '0 4px 15px rgba(0,0,0,0.1)',
                border: isCurrentPlan ? '3px solid #ff6b35' : '1px solid #e0e0e0',
                position: 'relative'
              }}
            >
              {isCurrentPlan && (
                <div style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  background: '#ff6b35',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '11px',
                  fontWeight: '700'
                }}>
                  CURRENT PLAN
                </div>
              )}

              {/* Header */}
              <div style={{
                background: getTierColor(plan.id),
                padding: '30px 25px',
                color: 'white'
              }}>
                <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>{plan.name}</h2>
                <div style={{ marginTop: '15px' }}>
                  <span style={{ fontSize: '42px', fontWeight: '700' }}>
                    ${plan.price}
                  </span>
                  {plan.price > 0 && <span style={{ fontSize: '16px', opacity: 0.9 }}>/month</span>}
                </div>
                {plan.annualPrice && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', opacity: 0.85 }}>
                    or ${plan.annualPrice}/year (save {Math.round((1 - plan.annualPrice / (plan.price * 12)) * 100)}%)
                  </p>
                )}
              </div>

              {/* Features */}
              <div style={{ padding: '25px' }}>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {plan.features.map((feature, i) => (
                    <li key={i} style={{
                      padding: '10px 0',
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: '14px',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}>
                      <span style={{ color: '#28a745', flexShrink: 0 }}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  disabled={isCurrentPlan || isDowngrade || (!PAID_PLANS_OPEN && plan.price > 0)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    marginTop: '25px',
                    background: isCurrentPlan 
                      ? '#e0e0e0' 
                      : isDowngrade 
                        ? '#f0f0f0' 
                        : getTierColor(plan.id),
                    color: isCurrentPlan || isDowngrade ? '#666' : 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '15px',
                    fontWeight: '700',
                    cursor: isCurrentPlan || isDowngrade || (!PAID_PLANS_OPEN && plan.price > 0) ? 'not-allowed' : 'pointer',
                    opacity: !PAID_PLANS_OPEN && plan.price > 0 && !isCurrentPlan ? 0.6 : 1
                  }}
                >
                  {isCurrentPlan 
                    ? 'Current Plan' 
                    : isDowngrade 
                      ? 'Downgrade' 
                      : plan.price === 0 
                        ? 'Free Forever' 
                        : PAID_PLANS_OPEN ? 'Upgrade Now' : 'Coming soon'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* FAQ */}
      <div style={{ 
        maxWidth: '700px', 
        margin: '60px auto 0',
        background: 'white',
        borderRadius: '16px',
        padding: '30px'
      }}>
        <h2 style={{ margin: '0 0 25px 0', fontSize: '20px', color: '#333', textAlign: 'center' }}>
          ❓ Frequently Asked Questions
        </h2>
        {[
          { q: 'Can I change plans anytime?', a: 'Yes! You can upgrade or downgrade at any time. Changes take effect immediately.' },
          { q: 'What happens to my data if I downgrade?', a: 'You\'ll be notified about what data will be deleted. Once confirmed, it\'s permanently removed.' },
          { q: 'Are there family plans?', a: 'Pro and Premium plans include family member support with customizable permissions.' },
          { q: 'How do Mango Credits work?', a: 'Mango Credits are our cryptocurrency. Earn them through purchases and use them at any Mango business.' },
        ].map((item, i) => (
          <div key={i} style={{ marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#333' }}>{item.q}</h4>
            <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: 1.5 }}>{item.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Upgrade;
